'use client';

import { useEffect, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { DeferredMount } from '@benzinga/hooks';
import { useWatchlistContext } from '@benzinga/user-context';

import { shouldShowHoldingsSlot } from './portfolioPageShell';
import { usePortfolioShellSnapshot, writePortfolioShellSnapshot } from './portfolioShellSnapshot';

import { EdgeContent } from './edge/EdgeContent';
import { EdgeSection } from './edge/EdgeSection';
import { FinancialTable } from './FinancialTable';
import { HoldingsTable } from './HoldingsTable';
import { PortfolioLinkModal } from './LinkPortfolio';
import { PortfolioPageHeader } from './PortfolioPageHeader';
import { PortfolioResearch } from './PortfolioResearch';
import { PortfolioSectionHeader } from './PortfolioSectionHeader';
import { buildPortfolioAllocations, sumPricedHoldingValues } from './portfolioActionUtils';
import { useHoldingPrices } from './useHoldingPrices';
import { WatchlistEmptyState } from './WatchlistEmptyState';
import { WatchlistPeers } from './WatchlistPeers';
import { WatchlistInsights } from './WatchlistInsights';
import {
  EdgeIdeasDeferredSkeleton,
  PeersDeferredSkeleton,
  PortfolioResearchDeferredSkeleton,
  WatchlistPanelSkeleton,
} from './WatchlistInsightsSkeleton';
import { WatchlistTopBar } from './TopBar';
import { PORTFOLIO_CARD_CLASSNAME } from './portfolioLayoutConstants';

export const PortfolioWorkspace = () => {
  const { isLoadingWatchlists, setShowYodleeModal, watchlists } = useWatchlistContext();
  const shellSnapshot = usePortfolioShellSnapshot();
  const pathname = usePathname();

  const { hasHoldings, isLoadingPrices, isLoadingSectors, pricedHoldings, sectorBySymbol } =
    useHoldingPrices(watchlists);
  const showHoldings = shouldShowHoldingsSlot(isLoadingWatchlists, hasHoldings, shellSnapshot.hasHoldings);

  const holdingValues = useMemo(() => sumPricedHoldingValues(pricedHoldings), [pricedHoldings]);

  const allocations = useMemo(
    () => (hasHoldings ? buildPortfolioAllocations(holdingValues.symbolValues, sectorBySymbol) : []),
    [hasHoldings, holdingValues.symbolValues, sectorBySymbol],
  );

  useEffect(() => {
    if (isLoadingWatchlists) {
      return;
    }
    writePortfolioShellSnapshot({ hasHoldings });
  }, [hasHoldings, isLoadingWatchlists]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('action') !== 'link_portfolio') {
      return;
    }
    setShowYodleeModal(true);
    params.delete('action');
    const query = params.toString();
    window.history.replaceState({}, '', `${pathname}${query ? `?${query}` : ''}`);
  }, [pathname, setShowYodleeModal]);

  return (
    <main className="min-h-screen bg-slate-50">
      <PortfolioPageHeader
        allocations={allocations}
        hasHoldings={showHoldings}
        holdingValues={holdingValues}
        isLoadingPrices={isLoadingPrices}
        isLoadingSectors={isLoadingSectors}
      />
      <div className="mx-auto flex max-w-[1400px] flex-col gap-4 px-4 py-3 sm:px-6">
        {showHoldings && (
          <section className={`${PORTFOLIO_CARD_CLASSNAME} min-h-[320px]`}>
            <PortfolioSectionHeader title="Holdings" subtitle="Positions from your linked brokerage" />
            {isLoadingWatchlists || isLoadingPrices ? (
              <div className="min-h-[240px] space-y-3 p-4" aria-hidden>
                <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
                <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
                <div className="h-4 w-5/6 animate-pulse rounded bg-slate-100" />
                <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
                <div className="h-4 w-4/5 animate-pulse rounded bg-slate-100" />
                <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
                <div className="h-4 w-3/4 animate-pulse rounded bg-slate-100" />
              </div>
            ) : (
              <HoldingsTable rows={pricedHoldings} />
            )}
          </section>
        )}

        <section id="portfolio-watchlists" className={PORTFOLIO_CARD_CLASSNAME}>
          {isLoadingWatchlists ? (
            <WatchlistPanelSkeleton />
          ) : watchlists.length === 0 ? (
            <div className="p-4">
              <WatchlistEmptyState />
            </div>
          ) : (
            <>
              <WatchlistTopBar />
              <div>
                <FinancialTable variant="watchlist" />
              </div>
              <div className="border-t border-slate-200 p-4">
                <WatchlistInsights />
              </div>
            </>
          )}
        </section>

        {!isLoadingWatchlists && watchlists.length > 0 && (
          <>
            <DeferredMount fallback={<EdgeIdeasDeferredSkeleton />}>
              <EdgeContent bannerType="insider-report">
                <EdgeSection />
              </EdgeContent>
            </DeferredMount>
            <DeferredMount fallback={<PortfolioResearchDeferredSkeleton />}>
              <PortfolioResearch />
            </DeferredMount>
            <EdgeContent bannerType="whisper-index" />
            <DeferredMount fallback={<PeersDeferredSkeleton />}>
              <WatchlistPeers />
            </DeferredMount>
          </>
        )}
      </div>
      <PortfolioLinkModal />
    </main>
  );
};
